// Single source of truth for AI backend authentication.
//
// Identity is a bot-verified session token (see ADR 2026-07-01, the "Connect account" flow
// in SettingsAI). Every request to the AI backend must go through aiAuthHeaders(); on a 401
// the token is stale/absent and must be cleared so the UI re-prompts to Connect.

import { getGlobal } from '../global';

import { selectSharedSettings } from '../global/selectors/sharedState';

const SESSION_TOKEN_KEY = 'ai_session_token';
// The session token is an HMAC identity for OUR backend only; never send it to an arbitrary
// user-set origin (a custom backend authenticates with its own API key, not our token).
const TRUSTED_BACKEND_HOSTS = new Set([
  'tg.meretrixnet.online', 'tg-dev.meretrixnet.online', 'localhost', '127.0.0.1',
]);

function isTrustedBackend(): boolean {
  const url = selectSharedSettings(getGlobal()).aiBackendUrl;
  // Empty = same-origin (relative /api/) — always our own co-located backend, so trusted.
  if (!url) return true;
  try {
    return TRUSTED_BACKEND_HOSTS.has(new URL(url).hostname);
  } catch {
    return false;
  }
}

// Base URL for OUR backend — ALWAYS the current origin so tg.* and tg-dev.* each hit their own
// co-located backend (matching session token; media plays same-origin so CSP 'self' allows it;
// nginx routes /api/ per domain). A persisted absolute aiBackendUrl to one of our hosts is legacy
// and deliberately ignored — it made a tg-dev page build prod URLs (cross-origin → CSP-blocked).
export function aiBackendBase(): string {
  const url = selectSharedSettings(getGlobal()).aiBackendUrl;
  if (url && !/(?:^|\/\/)(?:tg|tg-dev)\.meretrixnet\.online/.test(url)) return url;
  return window.location.origin;
}

export function getAiToken(): string {
  return localStorage.getItem(SESSION_TOKEN_KEY) || '';
}

export function setAiToken(token: string): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function clearAiToken(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
}

export function isAiConnected(): boolean {
  return Boolean(getAiToken());
}

// Auth headers for every AI backend request. Sends the bot-verified token; also sends the
// Telegram id, which the backend ignores once flipped but keeps working during rollout.
export function aiAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  // Only attach our identity to a trusted backend origin, so a (mis)configured aiBackendUrl
  // can never exfiltrate the session token.
  if (!isTrustedBackend()) return headers;
  const token = getAiToken();
  if (token) headers['X-Session-Token'] = token;
  const userId = getGlobal().currentUserId;
  if (userId) headers['X-Telegram-User-Id'] = String(userId);
  return headers;
}

// Returns true if the response indicates the token is no longer valid (401), clearing it so
// the UI reverts to "not connected".
export function handleAiUnauthorized(status: number): boolean {
  if (status === 401) {
    clearAiToken();
    return true;
  }
  return false;
}
